// Scopes a multi-day job to a single session for per-session tab views (Capture / Processing /
// Delivery). Pure function — returns { scopedJob, buildUpdate } or null if the session id doesn't
// match. Callers pair buildUpdate with their own updateJob dispatch. J5+ consumes this.

import { SESSION_CAPTURE_FIELDS, BLANK_SESSION } from "./utils.js";
import { SESSION_LOCATION_FIELDS } from "./constants.js";

// Fields that live on a session object. Used to split a patch into session-level and root-level
// writes inside buildUpdate. `captureDate` (root) and `date` (session) are intentionally distinct
// names for the same calendar day — session edits use `date`, root edits use `captureDate`.
const SESSION_FIELD_SET = new Set([
  ...SESSION_CAPTURE_FIELDS,
  ...SESSION_LOCATION_FIELDS,
  "sessionType", "date", "dayType",
  "callTime", "wrapTime", "notes",
  "isStudio",
]);

export function scopeJobToSession(job, sessionId) {
  if (!job || !sessionId) return null;
  const sessions = Array.isArray(job.sessions) ? job.sessions : [];
  const session = sessions.find(s => s?.id === sessionId);
  if (!session) return null;

  const blank = BLANK_SESSION();
  const scopedJob = { ...job };
  // Preserve the top-level job id explicitly for child collection reads in
  // session-scoped tab views (for example jobs/{rootJobId}/subjects).
  scopedJob.rootJobId = job.rootJobId || job.id;
  SESSION_CAPTURE_FIELDS.forEach(f => { scopedJob[f] = session[f] !== undefined ? session[f] : blank[f]; });
  SESSION_LOCATION_FIELDS.forEach(f => { scopedJob[f] = session[f] !== undefined ? session[f] : ""; });
  if (session.isStudio !== undefined) scopedJob.isStudio = session.isStudio;
  scopedJob.callTime = session.callTime || "";
  scopedJob.wrapTime = session.wrapTime || "";
  scopedJob.captureDate = session.date || "";

  // Splits a patch into session-level and root-level writes, then returns the combined shape
  // for updateJob(jobId, ...). Sessions always replaced wholesale to keep the call-site simple.
  const buildUpdate = (patch) => {
    const sessionPatch = {};
    const rootPatch = {};
    for (const [k, v] of Object.entries(patch || {})) {
      if (SESSION_FIELD_SET.has(k)) sessionPatch[k] = v;
      else rootPatch[k] = v;
    }
    if (Object.keys(sessionPatch).length === 0) return rootPatch;
    const nextSessions = sessions.map(s => s?.id === sessionId ? { ...s, ...sessionPatch } : s);
    return { ...rootPatch, sessions: nextSessions };
  };

  return { scopedJob, buildUpdate, session };
}
